
import type { Meta, StoryObj } from '@storybook/react'

import ClientIntegrationPage from './ClientIntegrationPage';
import Box from '@mui/material/Box';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { http, HttpResponse } from 'msw';
import OrbitProvider from 'providers/OrbitProvider';
import { within, userEvent, screen } from '@storybook/test';

const Template = (args) => {
  return (
    <OrbitProvider>
      <Box sx={{ minHeight: "2000px" }}>
        <QueryParamProvider adapter={ReactRouter6Adapter}>
          <ClientIntegrationPage {...args} />
        </QueryParamProvider>
      </Box>
    </OrbitProvider>
  )
};

const meta : Meta<typeof Template> = {
  title: "Pages/ClientIntegrationPage",
  component: Template,
};

type Story = StoryObj<typeof Template>;

export default meta

export const Default : Story = {
  render: Template,
  parameters: {
    orbit: {
      user: {
        email: 'testing@aol.com',
      },
    },
    reactRouter: {
      routePath: "/clients/:clientId/*",
      routeParams: { clientId: "31" },
      browserPath: "/clients/31/form-posting",
    },
    msw: {
      handlers: [
        http.get("/api/v1/clients/31/contracts", () => {
          return HttpResponse.json({
            "data": [
                {
                    "id": "60",
                    "type": "contracts",
                    "attributes": {
                        "vendor-code": null,
                        "payout": "cost_per_lead",
                        "can-post-leads": true
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "vendor": {
                            "data": {
                                "id": "1",
                                "type": "vendors"
                            }
                        }
                    }
                },
                {
                    "id": "289",
                    "type": "contracts",
                    "attributes": {
                        "vendor-code": null,
                        "payout": "cost_per_lead",
                        "can-post-leads": true
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "vendor": {
                            "data": {
                                "id": "2",
                                "type": "vendors"
                            }
                        }
                    }
                }
            ],
            "included": [
                {
                    "id": "31",
                    "type": "clients",
                    "attributes": {
                        "name": "UMA",
                        "created-at": "2019-12-10T16:38:10.216Z",
                        "updated-at": "2025-02-04T05:14:37.877Z",
                        "secondary-color": "#006ca7",
                        "primary-color": "#444a58",
                        "tcpa-disclosure": "By clicking the Request Info button, you agree to be contacted by phone, text message, or email via automated systems by Ultimate Medical Academy about your education at the email addresses and phone numbers you provided above, including any wireless number(s). You are not obligated to agree to automated contact to enroll. Note that even non-automated calls are recorded for quality assurance.",
                        "internal-name": "UMA Real",
                        "lp-email-sender": "Ultimate Medical Academy",
                        "lp-area-codes": "an 813 or 727 area code",
                        "lp-continue-url": "",
                        "implicit-tcpa": false,
                        "google-conversion-id": "w81DCM-K8sgBEN6X5OcC",
                        "complyed-client-id": null,
                        "campus-explorer-college-id": null,
                        "num-programs": 21,
                        "aor": false,
                        "contact-name": null,
                        "contact-email": null,
                        "contact-address": null,
                        "contact-city": null,
                        "contact-state": null,
                        "contact-zip": null,
                        "contact-phone": null,
                        "time-zone": "America/New_York",
                        "notes": "CREATIVE CLIENT-SPECIFIC GUIDELINES: UMA\n\nOverall:\n•\tAdvertising cannot use the internal school acronym (UMA) unless the name is clearly identified previously in the advertisement. \n              o\tLogo usage, in accordance with logo guidelines, suffices as the identifier of the full name.\n•\tAdvertising must clearly indicate that education or career training is offered and not employment or a \"career.\"\n•\tAs UMA is a school of education, the use of the term “training” should be used sparingly. Education is the preferred term instead of training. \n              o\tSome ideas include educational programs, educational institution, career training, training to be a pharmacy technician, healthcare and career education.\n•\tAll references to possible job opportunities upon completion of a career-focused program must include the term \"entry-level” or “pursue a career as a ….”\n•\tHealthcare is always shown as one word.\n\nCopy can’t include: \n•\tDo not use the term “free”, “at no cost” or any synonyms.\n•\tDon’t use Program name + “School” (Medical Billing and Coding School)\n•\tDon’t use an superlatives such as “Best” or “Great” or “Perfect”\n•\tDon’t use “Healthcare degree,” instead use “degree in Healthcare”\n•\tDo not use subjective language such as “affordable” \n•\tDo not use the term “classes”, instead use verbiage such as, “[Program] degree”\n       o \tUMA does not allow students to select class individually, students are required to complete all courses included in the program \n•\tDo not use “100% online” or “totally online”, instead “mostly online” or “majority online” \n      o\t       Some programs require labs be completed on campuses\n•\tAvoid using misleading language that cannot be proven \n•.      Don't say \"earn a degree\" say \"pursue\n•.      Don't say \"Are you older than 18?\"\n•.      Don't use \"at your own pace\"\n•.      Instead of \"dreams\", say goals\n\n\nPermissions:\n•\tIf copy is approved for one program, you can use it for any other program. The use of program names is interchangeable.\n•\t“Program name” and “degree in Healthcare” are also interchangeable in any approved copy\n•\tFlexibility to mix \u0026 match and/or “slim down” approved creative- \n              o\tWe have permission to mix \u0026 match sentences from already approved copy and remove sentences in order to make “new” copy without resubmitting for approval. \n              o\tPlease note this means that full sentences can be moved around or removed, but that you cannot edit the sentence. Only very minimal edits to the sentence are allowed if something grammatically off now that the sentences have been rearranged. \n              o\tThe way we were told to look at it is that every sentence is approved individually for use, not all the sentences together as a whole, that is why we can mix \u0026 match. \n•\tImages don’t have to be submitted for approval if the are similar to previously approved images. However, if the image is a new angle it needs to be submitted for approval.\n\nVisual:\n-Dancing must lean \"silly\". Any hint of \"suggestive dancing\" is not allowed\n-No animals in the videos\n-Imagery must me HC relevant ( no text tapping videos )\n",
                        "ingestion-endpoint": 0,
                        "metadata": {},
                        "limits-updated-at": "2025-02-04T05:14:37.702Z",
                        "response-time": "five_minutes",
                        "filtered-leads-client": false,
                        "agency-id": null,
                        "vanity-leads-hostname": null,
                        "vanity-leads-path": null,
                        "leads-endpoint-id": null,
                        "show-degree-program-changes": false,
                        "logo": "https://storage.googleapis.com/cygnus-development/3np1a4dvnceobfpwforqft6hyohc",
                        "compliance-logo": null,
                        "brand-guidelines-document": null,
                        "brand-guidelines-document-filename": null
                    },
                    "relationships": {
                        "leads-endpoint": {
                            "data": null
                        },
                        "organizations": {
                            "data": [
                                {
                                    "id": "5",
                                    "type": "organizations"
                                },
                                {
                                    "id": "108",
                                    "type": "organizations"
                                }
                            ]
                        },
                        "client-roles": {
                            "meta": {}
                        }
                    }
                },
                {
                    "id": "1",
                    "type": "vendors",
                    "attributes": {
                        "name": "Cygnus Education",
                        "created-at": "2021-01-25T01:26:50.313Z",
                        "updated-at": "2024-10-04T14:42:00.461Z",
                        "contact-name": null,
                        "contact-email": "jhutson@cygnuseducation.com",
                        "street-address": null,
                        "city": null,
                        "state": null,
                        "zip-code": null,
                        "phone": null,
                        "external-config": {},
                        "internal-for-client-id": null,
                        "time-zone-name": "America/New_York",
                        "permit-landing-pages": false,
                        "vendor-type": null
                    },
                    "relationships": {
                        "organizations": {
                            "meta": {}
                        }
                    }
                },
                {
                    "id": "2",
                    "type": "vendors",
                    "attributes": {
                        "name": "Internal",
                        "created-at": "2022-04-13T22:04:53.482Z",
                        "updated-at": "2022-04-13T22:04:53.482Z",
                        "contact-name": null,
                        "contact-email": null,
                        "street-address": null,
                        "city": null,
                        "state": null,
                        "zip-code": null,
                        "phone": null,
                        "external-config": {},
                        "internal-for-client-id": null,
                        "time-zone-name": "America/New_York",
                        "permit-landing-pages": false,
                        "vendor-type": null
                    },
                    "relationships": {
                        "organizations": {
                            "meta": {}
                        }
                    }
                }
            ]
          });
        }),
        http.get("/api/v1/contracts/60", () => {
          return HttpResponse.json({
            "data": {
                "id": "60",
                "type": "contracts",
                "attributes": {
                    "vendor-code": null,
                    "payout": "cost_per_lead",
                    "can-post-leads": true
                },
                "relationships": {
                    "client": {
                        "data": {
                            "id": "31",
                            "type": "clients"
                        }
                    },
                    "vendor": {
                        "data": {
                            "id": "1",
                            "type": "vendors"
                        }
                    }
                }
            },
            "included": [
                {
                    "id": "31",
                    "type": "clients",
                    "attributes": {
                        "name": "UMA",
                        "created-at": "2019-12-10T16:38:10.216Z",
                        "updated-at": "2025-02-04T05:14:37.877Z",
                        "secondary-color": "#006ca7",
                        "primary-color": "#444a58",
                        "tcpa-disclosure": "By clicking the Request Info button, you agree to be contacted by phone, text message, or email via automated systems by Ultimate Medical Academy about your education at the email addresses and phone numbers you provided above, including any wireless number(s). You are not obligated to agree to automated contact to enroll. Note that even non-automated calls are recorded for quality assurance.",
                        "internal-name": "UMA Real",
                        "lp-email-sender": "Ultimate Medical Academy",
                        "lp-area-codes": "an 813 or 727 area code",
                        "lp-continue-url": "",
                        "implicit-tcpa": false,
                        "google-conversion-id": "w81DCM-K8sgBEN6X5OcC",
                        "complyed-client-id": null,
                        "campus-explorer-college-id": null,
                        "num-programs": 21,
                        "aor": false,
                        "contact-name": null,
                        "contact-email": null,
                        "contact-address": null,
                        "contact-city": null,
                        "contact-state": null,
                        "contact-zip": null,
                        "contact-phone": null,
                        "time-zone": "America/New_York",
                        "notes": "CREATIVE CLIENT-SPECIFIC GUIDELINES: UMA\n\nOverall:\n•\tAdvertising cannot use the internal school acronym (UMA) unless the name is clearly identified previously in the advertisement. \n              o\tLogo usage, in accordance with logo guidelines, suffices as the identifier of the full name.\n•\tAdvertising must clearly indicate that education or career training is offered and not employment or a \"career.\"\n•\tAs UMA is a school of education, the use of the term “training” should be used sparingly. Education is the preferred term instead of training. \n              o\tSome ideas include educational programs, educational institution, career training, training to be a pharmacy technician, healthcare and career education.\n•\tAll references to possible job opportunities upon completion of a career-focused program must include the term \"entry-level” or “pursue a career as a ….”\n•\tHealthcare is always shown as one word.\n\nCopy can’t include: \n•\tDo not use the term “free”, “at no cost” or any synonyms.\n•\tDon’t use Program name + “School” (Medical Billing and Coding School)\n•\tDon’t use an superlatives such as “Best” or “Great” or “Perfect”\n•\tDon’t use “Healthcare degree,” instead use “degree in Healthcare”\n•\tDo not use subjective language such as “affordable” \n•\tDo not use the term “classes”, instead use verbiage such as, “[Program] degree”\n       o \tUMA does not allow students to select class individually, students are required to complete all courses included in the program \n•\tDo not use “100% online” or “totally online”, instead “mostly online” or “majority online” \n      o\t       Some programs require labs be completed on campuses\n•\tAvoid using misleading language that cannot be proven \n•.      Don't say \"earn a degree\" say \"pursue\n•.      Don't say \"Are you older than 18?\"\n•.      Don't use \"at your own pace\"\n•.      Instead of \"dreams\", say goals\n\n\nPermissions:\n•\tIf copy is approved for one program, you can use it for any other program. The use of program names is interchangeable.\n•\t“Program name” and “degree in Healthcare” are also interchangeable in any approved copy\n•\tFlexibility to mix \u0026 match and/or “slim down” approved creative- \n              o\tWe have permission to mix \u0026 match sentences from already approved copy and remove sentences in order to make “new” copy without resubmitting for approval. \n              o\tPlease note this means that full sentences can be moved around or removed, but that you cannot edit the sentence. Only very minimal edits to the sentence are allowed if something grammatically off now that the sentences have been rearranged. \n              o\tThe way we were told to look at it is that every sentence is approved individually for use, not all the sentences together as a whole, that is why we can mix \u0026 match. \n•\tImages don’t have to be submitted for approval if the are similar to previously approved images. However, if the image is a new angle it needs to be submitted for approval.\n\nVisual:\n-Dancing must lean \"silly\". Any hint of \"suggestive dancing\" is not allowed\n-No animals in the videos\n-Imagery must me HC relevant ( no text tapping videos )\n",
                        "ingestion-endpoint": 0,
                        "metadata": {},
                        "limits-updated-at": "2025-02-04T05:14:37.702Z",
                        "response-time": "five_minutes",
                        "filtered-leads-client": false,
                        "agency-id": null,
                        "vanity-leads-hostname": null,
                        "vanity-leads-path": null,
                        "leads-endpoint-id": null,
                        "show-degree-program-changes": false,
                        "logo": "https://storage.googleapis.com/cygnus-development/3np1a4dvnceobfpwforqft6hyohc",
                        "compliance-logo": null,
                        "brand-guidelines-document": null,
                        "brand-guidelines-document-filename": null
                    },
                    "relationships": {
                        "leads-endpoint": {
                            "data": null
                        }
                    }
                }
            ]
          });
        }),
        http.get("/api/v1/contracts/60/fields", () => {
          return HttpResponse.json({
            "data": [
                {
                    "id": "33",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "first_name",
                        "name": "FirstName",
                        "description": "First name",
                        "required": true,
                        "options": null,
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-02-08T00:40:26.179Z",
                        "updated-at": "2022-02-08T00:40:26.179Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "34",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "last_name",
                        "name": "LastName",
                        "description": "Last name",
                        "required": true,
                        "options": null,
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-02-08T00:40:26.192Z",
                        "updated-at": "2022-02-08T00:40:26.192Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "35",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "street_address",
                        "name": "StreetAddress",
                        "description": "Street address",
                        "required": true,
                        "options": null,
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-02-08T00:40:26.203Z",
                        "updated-at": "2022-02-08T00:40:26.203Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "36",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "city",
                        "name": "City",
                        "description": "City",
                        "required": true,
                        "options": null,
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-02-08T00:40:26.214Z",
                        "updated-at": "2022-02-08T00:40:26.214Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "37",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "state",
                        "name": "State",
                        "description": "State",
                        "required": true,
                        "options": null,
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-02-08T00:40:26.223Z",
                        "updated-at": "2022-02-08T00:40:26.223Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "38",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "zip_code",
                        "name": "ZIP_Code",
                        "description": "ZIP code",
                        "required": true,
                        "options": null,
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-02-08T00:40:26.239Z",
                        "updated-at": "2022-02-08T00:40:26.239Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "39",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "email",
                        "name": "Email",
                        "description": "Email",
                        "required": true,
                        "options": null,
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-02-08T00:40:26.250Z",
                        "updated-at": "2022-02-08T00:40:26.250Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "40",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "phone",
                        "name": "Phone",
                        "description": "Phone",
                        "required": true,
                        "options": null,
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-02-08T00:40:26.261Z",
                        "updated-at": "2022-02-08T00:40:26.261Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "41",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "custom_select",
                        "name": "StartClasses",
                        "description": "Start classes",
                        "required": true,
                        "options": [
                            {
                                "text": "Immediately",
                                "value": "Immediately"
                            },
                            {
                                "text": "1-3 Months",
                                "value": "1-3 Months"
                            },
                            {
                                "text": "3-6 Months",
                                "value": "3-6 Months"
                            },
                            {
                                "text": "More Than 6 Months",
                                "value": "More Than 6 Months"
                            }
                        ],
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-02-08T00:40:26.273Z",
                        "updated-at": "2022-02-08T00:40:26.273Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "1789",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "custom_select",
                        "name": "Citizen",
                        "description": "Citizen",
                        "required": false,
                        "options": [
                            {
                                "text": "Yes",
                                "value": "Yes"
                            },
                            {
                                "text": "No",
                                "value": "No"
                            }
                        ],
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-10-27T16:37:03.596Z",
                        "updated-at": "2022-10-27T16:37:03.596Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "1791",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "custom_text",
                        "name": "ProspectAge",
                        "description": "Age",
                        "required": false,
                        "options": null,
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-10-27T16:37:03.616Z",
                        "updated-at": "2022-10-27T16:37:03.616Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "1790",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "custom_select",
                        "name": "PreviousEducation",
                        "description": "Previous education",
                        "required": true,
                        "options": [
                            {
                                "text": "Did not finish high school or earn GED",
                                "value": "Non High School"
                            },
                            {
                                "text": "GED",
                                "value": "GED"
                            },
                            {
                                "text": "High school diploma",
                                "value": "High School"
                            },
                            {
                                "text": "Some college",
                                "value": "Some College"
                            },
                            {
                                "text": "College Graduate",
                                "value": "College Graduate"
                            }
                        ],
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-10-27T16:37:03.606Z",
                        "updated-at": "2023-07-17T17:25:53.681Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                },
                {
                    "id": "1788",
                    "type": "fields",
                    "attributes": {
                        "client-id": 31,
                        "field-type": "custom_select",
                        "name": "GraduationYear",
                        "description": "Graduation year",
                        "required": false,
                        "options": [
                            {
                                "text": "2024",
                                "value": "2024"
                            },
                            {
                                "text": "2023",
                                "value": "2023"
                            },
                            {
                                "text": "2022",
                                "value": "2022"
                            },
                            {
                                "text": "2021",
                                "value": "2021"
                            },
                            {
                                "text": "2020",
                                "value": "2020"
                            },
                            {
                                "text": "2019",
                                "value": "2019"
                            },
                            {
                                "text": "2018",
                                "value": "2018"
                            },
                            {
                                "text": "2017",
                                "value": "2017"
                            },
                            {
                                "text": "2016",
                                "value": "2016"
                            },
                            {
                                "text": "2015",
                                "value": "2015"
                            },
                            {
                                "text": "2014",
                                "value": "2014"
                            },
                            {
                                "text": "2013",
                                "value": "2013"
                            },
                            {
                                "text": "2012",
                                "value": "2012"
                            },
                            {
                                "text": "2011",
                                "value": "2011"
                            },
                            {
                                "text": "2010",
                                "value": "2010"
                            },
                            {
                                "text": "2009",
                                "value": "2009"
                            },
                            {
                                "text": "2008",
                                "value": "2008"
                            },
                            {
                                "text": "2007",
                                "value": "2007"
                            },
                            {
                                "text": "2006",
                                "value": "2006"
                            },
                            {
                                "text": "2005",
                                "value": "2005"
                            },
                            {
                                "text": "2004",
                                "value": "2004"
                            },
                            {
                                "text": "2003",
                                "value": "2003"
                            },
                            {
                                "text": "2002",
                                "value": "2002"
                            },
                            {
                                "text": "2001",
                                "value": "2001"
                            },
                            {
                                "text": "2000",
                                "value": "2000"
                            },
                            {
                                "text": "1999",
                                "value": "1999"
                            },
                            {
                                "text": "1998",
                                "value": "1998"
                            },
                            {
                                "text": "1997",
                                "value": "1997"
                            },
                            {
                                "text": "1996",
                                "value": "1996"
                            },
                            {
                                "text": "1995",
                                "value": "1995"
                            },
                            {
                                "text": "1994",
                                "value": "1994"
                            },
                            {
                                "text": "1993",
                                "value": "1993"
                            },
                            {
                                "text": "1992",
                                "value": "1992"
                            },
                            {
                                "text": "1991",
                                "value": "1991"
                            },
                            {
                                "text": "1990",
                                "value": "1990"
                            },
                            {
                                "text": "1989",
                                "value": "1989"
                            },
                            {
                                "text": "1988",
                                "value": "1988"
                            },
                            {
                                "text": "1987",
                                "value": "1987"
                            },
                            {
                                "text": "1986",
                                "value": "1986"
                            },
                            {
                                "text": "1985",
                                "value": "1985"
                            },
                            {
                                "text": "1984",
                                "value": "1984"
                            },
                            {
                                "text": "1983",
                                "value": "1983"
                            },
                            {
                                "text": "1982",
                                "value": "1982"
                            },
                            {
                                "text": "1981",
                                "value": "1981"
                            },
                            {
                                "text": "1980",
                                "value": "1980"
                            },
                            {
                                "text": "1979",
                                "value": "1979"
                            },
                            {
                                "text": "1978",
                                "value": "1978"
                            },
                            {
                                "text": "1977",
                                "value": "1977"
                            },
                            {
                                "text": "1976",
                                "value": "1976"
                            },
                            {
                                "text": "1975",
                                "value": "1975"
                            },
                            {
                                "text": "1974",
                                "value": "1974"
                            },
                            {
                                "text": "1973",
                                "value": "1973"
                            },
                            {
                                "text": "1972",
                                "value": "1972"
                            },
                            {
                                "text": "1971",
                                "value": "1971"
                            },
                            {
                                "text": "1970",
                                "value": "1970"
                            },
                            {
                                "text": "1969",
                                "value": "1969"
                            },
                            {
                                "text": "1968",
                                "value": "1968"
                            },
                            {
                                "text": "1967",
                                "value": "1967"
                            },
                            {
                                "text": "Other",
                                "value": "Other"
                            },
                            {
                                "text": "No HS Diploma Yet",
                                "value": "No HS Diploma Yet"
                            }
                        ],
                        "dependent-on-field-id": null,
                        "dependent-on-values": null,
                        "created-at": "2022-10-27T16:37:03.566Z",
                        "updated-at": "2024-06-11T18:22:50.008Z",
                        "private": false,
                        "groupable": true,
                        "aggregate": null,
                        "mutable": false,
                        "show-changes": false
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        }
                    }
                }
            ]
          });
        }),
        http.get("/api/v1/contracts/60/client-campaigns", () => {
          return HttpResponse.json({
            "data": [
                {
                    "id": "223",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "af1021a7",
                        "name": "Medical Administrative Assistant - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2022-02-28T16:10:26.150Z",
                        "custom-attributes": {
                            "LeadSource": "CYGSOC"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "208",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "515",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "4e84426d",
                        "name": "Medical Billing and Coding - Platinum - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2022-05-31T15:15:32.819Z",
                        "custom-attributes": {
                            "LeadSource": "CYGTIER2"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "7",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "517",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "697f7693",
                        "name": "Healthcare Accounting - Platinum - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2022-05-31T16:09:11.117Z",
                        "custom-attributes": {
                            "LeadSource": "CYGTIER2"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "8",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "7",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "2e0e71f",
                        "name": "Medical Billing and Coding - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2021-09-28T00:39:33.517Z",
                        "custom-attributes": {
                            "LeadSource": "CYGSOC"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "7",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "519",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "b68a702e",
                        "name": "Health Information Management - Platinum - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2022-05-31T16:10:45.966Z",
                        "custom-attributes": {
                            "LeadSource": "CYGTIER2"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "163",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "649",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "b53a027f",
                        "name": "Pharmacy Technician - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2023-02-28T22:47:53.347Z",
                        "custom-attributes": {
                            "LeadSource": "CYGSOC"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "343",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "566",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "8e8792ab",
                        "name": "Clearwater - All - Shared",
                        "campaign-type": "shared",
                        "status": "active",
                        "created-at": "2022-09-01T20:55:10.284Z",
                        "custom-attributes": {},
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "245",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "523",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "72bedf6f",
                        "name": "Healthcare Management - Tier 2 - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2022-05-31T23:19:33.896Z",
                        "custom-attributes": {
                            "LeadSource": "CYGTIER"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "211",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "108",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "35263a6",
                        "name": "Medical Billing and Coding - Shared (Historical)",
                        "campaign-type": "shared",
                        "status": "active",
                        "created-at": "2021-09-28T00:39:33.517Z",
                        "custom-attributes": {
                            "LeadSource": "CYGSHR"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "7",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "231",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "51a94854",
                        "name": "Healthcare Management - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2022-03-30T22:39:16.534Z",
                        "custom-attributes": {
                            "LeadSource": "CYGSOC"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "211",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "62",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "988989e",
                        "name": "Other - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2021-09-28T00:39:33.517Z",
                        "custom-attributes": {},
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "126",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "109",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "d9f7f71",
                        "name": "Healthcare Accounting - Shared",
                        "campaign-type": "shared",
                        "status": "active",
                        "created-at": "2021-09-28T00:39:33.517Z",
                        "custom-attributes": {
                            "LeadSource": "CYGSHR"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "8",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "199",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "4db7235",
                        "name": "Health Information Management - Shared",
                        "campaign-type": "shared",
                        "status": "active",
                        "created-at": "2021-09-28T00:39:33.517Z",
                        "custom-attributes": {
                            "LeadSource": "CYGSHR"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "163",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "163",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "f295762",
                        "name": "Other - Shared",
                        "campaign-type": "shared",
                        "status": "active",
                        "created-at": "2021-09-28T00:39:33.517Z",
                        "custom-attributes": {},
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "126",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "650",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "d510a5a9",
                        "name": "Pharmacy Technician - Platinum - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2023-02-28T22:48:25.174Z",
                        "custom-attributes": {
                            "LeadSource": "CYGTIER2"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "343",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "651",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "1af6a635",
                        "name": "Pharmacy Technician - Tier 2 - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2023-02-28T22:48:48.512Z",
                        "custom-attributes": {
                            "LeadSource": "CYGTIER"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "343",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "524",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "6d07e13c",
                        "name": "Health Information Management - Tier 2 - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2022-05-31T23:21:23.021Z",
                        "custom-attributes": {
                            "LeadSource": "CYGTIER"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "163",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "970",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "8788c6e3",
                        "name": "Medical Billing and Coding - Shared (Exclusive)",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2024-03-27T15:15:41.034Z",
                        "custom-attributes": {
                            "LeadSource": "CYGSHR"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "7",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "205",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "ed8708b",
                        "name": "Medical Billing and Coding - Tier 2 - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2021-10-27T20:46:52.104Z",
                        "custom-attributes": {
                            "LeadSource": "CYGTIER"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "7",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "8",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "d4e6dde",
                        "name": "Healthcare Accounting - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2021-09-28T00:39:33.517Z",
                        "custom-attributes": {
                            "LeadSource": "CYGSOC"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "8",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "522",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "0edd40a0",
                        "name": "Medical Administrative Assistant - Tier 2 - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2022-05-31T23:17:31.552Z",
                        "custom-attributes": {
                            "LeadSource": "CYGTIER"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "208",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "518",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "bee8d3e6",
                        "name": "Medical Administrative Assistant - Platinum - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2022-05-31T16:09:47.407Z",
                        "custom-attributes": {
                            "LeadSource": "CYGTIER2"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "208",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "98",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "db713dd",
                        "name": "Health Information Management - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2021-09-28T00:39:33.517Z",
                        "custom-attributes": {
                            "LeadSource": "CYGSOC"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "163",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "516",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "8d360ac8",
                        "name": "Healthcare Management - Platinum - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2022-05-31T16:08:37.828Z",
                        "custom-attributes": {
                            "lead-source": "CYGTIER2"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "211",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "206",
                    "type": "client-campaigns",
                    "attributes": {
                        "public-id": "6a0d64d",
                        "name": "Healthcare Accounting - Tier 2 - Exclusive",
                        "campaign-type": "exclusive",
                        "status": "active",
                        "created-at": "2021-10-27T20:48:22.389Z",
                        "custom-attributes": {
                            "LeadSource": "CYGTIER"
                        },
                        "smart-score": null,
                        "smart-score-explanation": null
                    },
                    "relationships": {
                        "contract": {
                            "data": {
                                "id": "60",
                                "type": "contracts"
                            }
                        },
                        "program-group": {
                            "data": {
                                "id": "8",
                                "type": "program-groups"
                            }
                        }
                    }
                }
            ]
          });
        }),
        http.get("/api/v1/contracts/60/degree-programs*", () => {
          return HttpResponse.json({
            "data": [
                {
                    "id": "732",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Clearwater - Dental Assistant",
                        "created-at": "2022-09-01T19:17:13.154Z",
                        "updated-at": "2022-11-09T00:04:15.602Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 245,
                        "program-code": "DA",
                        "uuid": "fb40ecac-1313-4ef3-9a03-03570dfafdeb",
                        "public-id": "c9ad38e6",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8691",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8692",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8693",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8695",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8694",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8690",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "9182",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "245",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "733",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Clearwater - Medical Assistant",
                        "created-at": "2022-09-02T02:52:09.395Z",
                        "updated-at": "2022-11-09T00:04:15.617Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 245,
                        "program-code": "MA",
                        "uuid": "7f799468-3256-4b1a-b37f-dc9768708075",
                        "public-id": "82f7ba3a",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8697",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8698",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8700",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8701",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8699",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8696",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "9183",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "245",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "627",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eHealth Information Management\u003c/span\u003e Program",
                        "created-at": "2022-06-23T17:42:21.964Z",
                        "updated-at": "2022-11-09T00:04:15.715Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 163,
                        "program-code": "HIM",
                        "uuid": "24c7315a-b646-4354-96a4-8c3c1c57d3ef",
                        "public-id": "e0b6445f",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": []
                        },
                        "program-group": {
                            "data": {
                                "id": "163",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "433",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eHealth Information Management\u003c/span\u003e Program",
                        "created-at": "2021-09-04T01:25:13.301Z",
                        "updated-at": "2022-11-09T00:04:15.671Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 163,
                        "program-code": "HIM",
                        "uuid": "35238203-1871-e690-2c3b-2431eaf0abbd",
                        "public-id": "9770c0a",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8717",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8718",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8716",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "9185",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "163",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "71",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eHealthcare Degrees\u003c/span\u003e \u0026 Programs",
                        "created-at": "2020-02-21T15:40:37.494Z",
                        "updated-at": "2021-09-07T15:38:27.391Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 126,
                        "program-code": null,
                        "uuid": "bcb8acb2-c674-519c-7746-d6bc02700e5d",
                        "public-id": "37db9c1",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8689",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8688",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "126",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "629",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eHealthcare Management\u003c/span\u003e Program",
                        "created-at": "2022-06-23T18:03:15.632Z",
                        "updated-at": "2022-11-09T00:04:15.761Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 211,
                        "program-code": "HCM",
                        "uuid": "077c3c81-f0a6-44d7-8695-aefd95e3eb7c",
                        "public-id": "2831a2f8",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": []
                        },
                        "program-group": {
                            "data": {
                                "id": "211",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "555",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eHealthcare Management\u003c/span\u003e Program",
                        "created-at": "2022-03-30T22:38:39.325Z",
                        "updated-at": "2022-11-09T00:04:15.773Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 211,
                        "program-code": "HCM",
                        "uuid": "3dc778b8-d514-4932-a74f-0fb87b8ee446",
                        "public-id": "9f44b8c1",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8734",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8735",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8737",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "9187",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "211",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "282",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eHealthcare Management\u003c/span\u003e Program",
                        "created-at": "2020-11-06T18:03:01.066Z",
                        "updated-at": "2022-05-06T01:24:05.669Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 126,
                        "program-code": null,
                        "uuid": "8e85c1e9-ac3e-93fc-0b1e-299e79e399c6",
                        "public-id": "f08c3dd",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8746",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8747",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8748",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "126",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "626",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eHealthcare\u003c/span\u003e Programs",
                        "created-at": "2022-06-23T17:36:08.494Z",
                        "updated-at": "2022-11-09T00:04:15.693Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 8,
                        "program-code": "HCA",
                        "uuid": "9a26991a-3e26-46f4-933a-37bd1e978daf",
                        "public-id": "d0b57d54",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": []
                        },
                        "program-group": {
                            "data": {
                                "id": "8",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "172",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eHealthcare\u003c/span\u003e Programs",
                        "created-at": "2020-09-22T21:48:16.054Z",
                        "updated-at": "2022-11-09T00:04:15.660Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 8,
                        "program-code": "HCA",
                        "uuid": "7401a340-81d3-01e3-64f1-37b3df6ae780",
                        "public-id": "c8f7b2b",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8714",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8715",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8713",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "8",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "110",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eHealthcare\u003c/span\u003e Programs",
                        "created-at": "2020-03-16T19:35:06.075Z",
                        "updated-at": "2022-11-09T00:04:15.786Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 8,
                        "program-code": "HCA",
                        "uuid": "cfda8acd-8b74-04b4-02eb-db7554f4aa21",
                        "public-id": "6525d6a",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8740",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8741",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8742",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8743",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "8",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "516",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eMedical Administrative Assistant\u003c/span\u003e Programs",
                        "created-at": "2022-02-25T16:43:08.549Z",
                        "updated-at": "2022-11-09T00:04:15.727Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 208,
                        "program-code": "HS-MAA",
                        "uuid": "906b9732-84d4-429c-a085-78686588468e",
                        "public-id": "de0ca384",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8726",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8727",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8728",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8729",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "208",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "628",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eMedical Administrative Assistant\u003c/span\u003e Programs",
                        "created-at": "2022-06-23T18:00:03.969Z",
                        "updated-at": "2022-11-09T00:04:15.740Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 208,
                        "program-code": "HS-MAA",
                        "uuid": "480ed00c-285a-47e5-b0a7-ed08923fc8cb",
                        "public-id": "d26d7056",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": []
                        },
                        "program-group": {
                            "data": {
                                "id": "208",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "35",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eMedical Billing and Coding\u003c/span\u003e Programs",
                        "created-at": "2019-12-10T16:38:45.361Z",
                        "updated-at": "2022-11-17T21:56:15.040Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 7,
                        "program-code": "MBCOA",
                        "uuid": "18a80d03-b569-5b8e-240f-c4b992888e22",
                        "public-id": "df1bed4",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8711",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8707",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8706",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8709",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "7",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "171",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eMedical Billing and Coding\u003c/span\u003e Programs",
                        "created-at": "2020-09-18T20:19:44.691Z",
                        "updated-at": "2022-11-09T00:04:15.634Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 7,
                        "program-code": "MBCOA",
                        "uuid": "186899c6-86b3-e4f1-352c-858a00c975e1",
                        "public-id": "49fc7e2",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8704",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8703",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8705",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8702",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "7",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "625",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eMedical Billing and Coding\u003c/span\u003e Programs",
                        "created-at": "2022-06-23T16:42:06.711Z",
                        "updated-at": "2022-11-09T00:04:15.682Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 7,
                        "program-code": "MBCOA",
                        "uuid": "456ba94e-b096-433e-a61b-319d882da015",
                        "public-id": "cd0edf53",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": []
                        },
                        "program-group": {
                            "data": {
                                "id": "7",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "271",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003eMedical Billing and Coding\u003c/span\u003e Programs",
                        "created-at": "2020-11-04T17:56:11.681Z",
                        "updated-at": "2022-11-09T00:04:15.704Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 7,
                        "program-code": "MBCOA",
                        "uuid": "cefc467f-a323-dc5a-c51c-d1146da97f42",
                        "public-id": "048092e",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8722",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8724",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8723",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8725",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "7",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "163",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online \u003cspan class=\"secondary-color\"\u003ePharmacy Technician\u003c/span\u003e Program",
                        "created-at": "2020-07-13T16:31:00.638Z",
                        "updated-at": "2022-11-09T00:04:15.749Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 126,
                        "program-code": "TEST",
                        "uuid": "ce133a33-bd55-5987-e8a4-005ccd32d962",
                        "public-id": "128a66b",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8732",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8733",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "126",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "888",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online Health Sciences - Pharmacy Technician Program with On-Site Externship¹",
                        "created-at": "2023-02-28T19:52:45.342Z",
                        "updated-at": "2023-06-01T15:26:10.409Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 343,
                        "program-code": "HS-PHT",
                        "uuid": "aefa5419-e1dc-499c-99f7-74c463696919",
                        "public-id": "9b47aa63",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "9541",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "9540",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "9539",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "9538",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "343",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "394",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online Healthcare Accounting",
                        "created-at": "2021-05-18T19:43:39.630Z",
                        "updated-at": "2021-05-23T14:18:16.166Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 126,
                        "program-code": null,
                        "uuid": "f311f5b1-0ccd-1e85-ab37-8c6636b9c341",
                        "public-id": "ab3e36f",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": []
                        },
                        "program-group": {
                            "data": {
                                "id": "126",
                                "type": "program-groups"
                            }
                        }
                    }
                },
                {
                    "id": "439",
                    "type": "degree-programs",
                    "attributes": {
                        "client-id": 31,
                        "name": "Online Healthcare Accounting Program",
                        "created-at": "2021-11-02T17:40:25.489Z",
                        "updated-at": "2022-11-09T00:04:15.590Z",
                        "campus-explorer-program-id": null,
                        "program-group-id": 8,
                        "program-code": "HCA",
                        "uuid": "a9c2cedc-266b-8ef4-032e-0ccb9eb26732",
                        "public-id": "1ffdc7b",
                        "custom-attributes": {},
                        "lp-continue-url": null,
                        "status": "active"
                    },
                    "relationships": {
                        "client": {
                            "data": {
                                "id": "31",
                                "type": "clients"
                            }
                        },
                        "exclusion-rules": {
                            "data": [
                                {
                                    "id": "8682",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8684",
                                    "type": "exclusion-rules"
                                },
                                {
                                    "id": "8683",
                                    "type": "exclusion-rules"
                                }
                            ]
                        },
                        "program-group": {
                            "data": {
                                "id": "8",
                                "type": "program-groups"
                            }
                        }
                    }
                }
            ],
            "included": [
                {
                    "id": "8691",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "greater_than",
                        "value": [
                            "54"
                        ],
                        "position": 9,
                        "updated-at": "2022-10-27T16:37:03.890Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1791",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8692",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "not_in",
                        "value": [
                            "33677",
                            "33701",
                            "33702",
                            "33703",
                            "33704",
                            "33705",
                            "33706",
                            "33707",
                            "33708",
                            "33709",
                            "33710",
                            "33711",
                            "33712",
                            "33713",
                            "33714",
                            "33715",
                            "33716",
                            "33730",
                            "33731",
                            "33732",
                            "33733",
                            "33734",
                            "33736",
                            "33737",
                            "33738",
                            "33740",
                            "33741",
                            "33742",
                            "33743",
                            "33744",
                            "33747",
                            "33755",
                            "33756",
                            "33757",
                            "33758",
                            "33759",
                            "33760",
                            "33761",
                            "33762",
                            "33763",
                            "33764",
                            "33765",
                            "33766",
                            "33767",
                            "33770",
                            "33771",
                            "33772",
                            "33773",
                            "33774",
                            "33775",
                            "33776",
                            "33777",
                            "33778",
                            "33779",
                            "33780",
                            "33781",
                            "33782",
                            "33784",
                            "33785",
                            "33786",
                            "34660",
                            "34677",
                            "34681",
                            "34682",
                            "34683",
                            "34684",
                            "34685",
                            "34688",
                            "34689",
                            "34690",
                            "34691",
                            "34695",
                            "34697",
                            "34698"
                        ],
                        "position": 10,
                        "updated-at": "2022-10-27T16:37:03.902Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "38",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8693",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "More Than 6 Months"
                        ],
                        "position": 11,
                        "updated-at": "2022-10-27T16:37:03.928Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "41",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8695",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 13,
                        "updated-at": "2022-10-27T16:37:03.956Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8694",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 12,
                        "updated-at": "2022-11-09T00:04:10.692Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8690",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Other",
                            "No HS Diploma Yet"
                        ],
                        "position": 8,
                        "updated-at": "2022-10-28T15:42:54.646Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1788",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "9182",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "less_than",
                        "value": [
                            "1986"
                        ],
                        "position": 14,
                        "updated-at": "2022-11-04T20:22:49.094Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1788",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8697",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "greater_than",
                        "value": [
                            "54"
                        ],
                        "position": 9,
                        "updated-at": "2022-10-27T16:37:03.999Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1791",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8698",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "More Than 6 Months"
                        ],
                        "position": 10,
                        "updated-at": "2022-10-27T16:37:04.011Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "41",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8700",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 12,
                        "updated-at": "2022-10-27T16:37:04.039Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8701",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "not_in",
                        "value": [
                            "33677",
                            "33701",
                            "33702",
                            "33703",
                            "33704",
                            "33705",
                            "33706",
                            "33707",
                            "33708",
                            "33709",
                            "33710",
                            "33711",
                            "33712",
                            "33713",
                            "33714",
                            "33715",
                            "33716",
                            "33730",
                            "33731",
                            "33732",
                            "33733",
                            "33734",
                            "33736",
                            "33737",
                            "33738",
                            "33740",
                            "33741",
                            "33742",
                            "33743",
                            "33744",
                            "33747",
                            "33755",
                            "33756",
                            "33757",
                            "33758",
                            "33759",
                            "33760",
                            "33761",
                            "33762",
                            "33763",
                            "33764",
                            "33765",
                            "33766",
                            "33767",
                            "33770",
                            "33771",
                            "33772",
                            "33773",
                            "33774",
                            "33775",
                            "33776",
                            "33777",
                            "33778",
                            "33779",
                            "33780",
                            "33781",
                            "33782",
                            "33784",
                            "33785",
                            "33786",
                            "34660",
                            "34677",
                            "34681",
                            "34682",
                            "34683",
                            "34684",
                            "34685",
                            "34688",
                            "34689",
                            "34690",
                            "34691",
                            "34695",
                            "34697",
                            "34698"
                        ],
                        "position": 13,
                        "updated-at": "2022-10-27T16:37:04.052Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "38",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8699",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 11,
                        "updated-at": "2022-11-09T00:04:11.150Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8696",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Other",
                            "No HS Diploma Yet"
                        ],
                        "position": 8,
                        "updated-at": "2022-10-28T15:42:54.684Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1788",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "9183",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "less_than",
                        "value": [
                            "1986"
                        ],
                        "position": 14,
                        "updated-at": "2022-11-04T20:22:49.129Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1788",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8717",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 18,
                        "updated-at": "2022-10-27T16:37:04.337Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8718",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "CT",
                            "LA",
                            "MA",
                            "MN",
                            "NV",
                            "NH",
                            "NY",
                            "NC",
                            "ND",
                            "OK",
                            "RI",
                            "TN",
                            "WA",
                            "DC"
                        ],
                        "position": 19,
                        "updated-at": "2022-10-27T16:37:04.350Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "37",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8716",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 17,
                        "updated-at": "2022-11-09T00:04:13.000Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "9185",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Other",
                            "No HS Diploma Yet"
                        ],
                        "position": 20,
                        "updated-at": "2023-10-25T10:50:47.241Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1788",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8689",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 4,
                        "updated-at": "2022-10-27T16:37:03.859Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8688",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 3,
                        "updated-at": "2022-11-09T00:04:10.080Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8734",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 14,
                        "updated-at": "2022-10-27T16:37:04.666Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8735",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 15,
                        "updated-at": "2022-11-09T00:04:14.728Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8737",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "CT",
                            "MA",
                            "NY",
                            "ND",
                            "RI",
                            "DC"
                        ],
                        "position": 16,
                        "updated-at": "2023-10-25T10:51:47.170Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "37",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "9187",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Other",
                            "No HS Diploma Yet"
                        ],
                        "position": 17,
                        "updated-at": "2023-10-25T10:51:47.164Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1788",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8746",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 4,
                        "updated-at": "2022-11-09T00:04:15.563Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8747",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "NY",
                            "CT",
                            "RI",
                            "DC",
                            "MA"
                        ],
                        "position": 5,
                        "updated-at": "2022-10-27T16:37:04.916Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "37",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8748",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 6,
                        "updated-at": "2022-10-27T16:37:04.931Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8714",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "NY",
                            "CT",
                            "RI",
                            "DC",
                            "MA"
                        ],
                        "position": 5,
                        "updated-at": "2022-10-27T16:37:04.293Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "37",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8715",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 6,
                        "updated-at": "2022-10-27T16:37:04.306Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8713",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 4,
                        "updated-at": "2022-11-09T00:04:12.552Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8740",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 15,
                        "updated-at": "2022-11-09T00:04:15.154Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8741",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "CT",
                            "MA",
                            "NY",
                            "ND",
                            "RI",
                            "DC"
                        ],
                        "position": 16,
                        "updated-at": "2022-10-27T16:37:04.797Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "37",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8742",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 17,
                        "updated-at": "2022-10-27T16:37:04.812Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8743",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Other",
                            "No HS Diploma Yet"
                        ],
                        "position": 18,
                        "updated-at": "2022-10-28T15:42:55.162Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1788",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8726",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 14,
                        "updated-at": "2022-11-09T00:04:13.881Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8727",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "CT",
                            "MA",
                            "NY",
                            "ND",
                            "RI",
                            "DC"
                        ],
                        "position": 15,
                        "updated-at": "2022-10-27T16:37:04.519Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "37",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8728",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 16,
                        "updated-at": "2022-10-27T16:37:04.534Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8729",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Other",
                            "No HS Diploma Yet"
                        ],
                        "position": 17,
                        "updated-at": "2022-10-28T15:42:55.011Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1788",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8711",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Other",
                            "No HS Diploma Yet"
                        ],
                        "position": 19,
                        "updated-at": "2023-10-25T10:48:27.708Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1788",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8707",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 17,
                        "updated-at": "2022-11-17T21:56:58.415Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8706",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 16,
                        "updated-at": "2022-11-17T21:56:58.415Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8709",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "CT",
                            "MA",
                            "NY",
                            "ND",
                            "RI",
                            "DC"
                        ],
                        "position": 18,
                        "updated-at": "2022-11-17T21:56:58.415Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "37",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8704",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "28574",
                            "37012",
                            "16503",
                            "95965",
                            "38761",
                            "84078",
                            "35905",
                            "88240",
                            "71603",
                            "65270",
                            "02720",
                            "46516",
                            "88310",
                            "03261",
                            "35903",
                            "67530",
                            "46514",
                            "70525",
                            "04096",
                            "88242",
                            "71371",
                            "38501",
                            "54932",
                            "84780"
                        ],
                        "position": 7,
                        "updated-at": "2022-11-04T20:22:49.162Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "38",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8703",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "MA",
                            "DC",
                            "RI",
                            "CT",
                            "NY",
                            "ND"
                        ],
                        "position": 6,
                        "updated-at": "2022-10-27T16:37:04.083Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "37",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8705",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 8,
                        "updated-at": "2022-10-27T16:37:04.115Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8702",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 5,
                        "updated-at": "2022-11-09T00:04:11.606Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8722",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 5,
                        "updated-at": "2022-10-27T16:37:04.445Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8724",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "MA",
                            "DC",
                            "RI",
                            "CT",
                            "NY",
                            "ND"
                        ],
                        "position": 7,
                        "updated-at": "2022-10-27T16:37:04.472Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "37",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8723",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 6,
                        "updated-at": "2022-11-09T00:04:13.417Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8725",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "28574",
                            "37012",
                            "16503",
                            "95965",
                            "38761",
                            "84078",
                            "35905",
                            "88240",
                            "71603",
                            "65270",
                            "02720",
                            "46516",
                            "88310",
                            "03261",
                            "35903",
                            "67530",
                            "46514",
                            "70525",
                            "04096",
                            "88242",
                            "71371",
                            "38501",
                            "54932",
                            "84780"
                        ],
                        "position": 8,
                        "updated-at": "2022-11-04T20:22:49.294Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "38",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8732",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 3,
                        "updated-at": "2022-10-27T16:37:04.633Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8733",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 4,
                        "updated-at": "2022-11-09T00:04:14.303Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "9541",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "CA",
                            "CT",
                            "DC",
                            "MA",
                            "ND",
                            "NY",
                            "RI",
                            "LA",
                            "MD",
                            "ME",
                            "MN",
                            "NC",
                            "NH",
                            "NM",
                            "NV",
                            "OH",
                            "OK",
                            "SC",
                            "TN",
                            "UT",
                            "VA",
                            "WA"
                        ],
                        "position": 9,
                        "updated-at": "2023-10-25T10:50:15.968Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "37",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "9540",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 8,
                        "updated-at": "2023-10-25T10:50:15.973Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "9539",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 7,
                        "updated-at": "2023-10-25T10:50:15.980Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "9538",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Other",
                            "No HS Diploma Yet"
                        ],
                        "position": 6,
                        "updated-at": "2023-10-25T10:50:15.984Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1788",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8682",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "Non High School"
                        ],
                        "position": 14,
                        "updated-at": "2022-10-27T16:37:03.759Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1790",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8684",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "CT",
                            "IA",
                            "MA",
                            "NE",
                            "NY",
                            "ND",
                            "RI",
                            "UT",
                            "DC"
                        ],
                        "position": 16,
                        "updated-at": "2022-10-27T16:37:03.789Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "37",
                                "type": "fields"
                            }
                        }
                    }
                },
                {
                    "id": "8683",
                    "type": "exclusion-rules",
                    "attributes": {
                        "operator": "in",
                        "value": [
                            "No"
                        ],
                        "position": 15,
                        "updated-at": "2022-11-09T00:04:09.454Z"
                    },
                    "relationships": {
                        "field": {
                            "data": {
                                "id": "1789",
                                "type": "fields"
                            }
                        }
                    }
                }
            ]
          });
        }),
        http.get("/api/v1/clients/31/exclusion-rules", () => {
          return HttpResponse.json({
              "data": [
                  {
                      "id": "9693",
                      "type": "exclusion-rules",
                      "attributes": {
                          "operator": "in",
                          "value": [
                              "VA"
                          ],
                          "position": 7,
                          "updated-at": "2024-08-13T13:57:04.912Z"
                      },
                      "relationships": {
                          "field": {
                              "data": {
                                  "id": "37",
                                  "type": "fields"
                              }
                          }
                      }
                  },
                  {
                      "id": "8680",
                      "type": "exclusion-rules",
                      "attributes": {
                          "operator": "in",
                          "value": [
                              "No"
                          ],
                          "position": 5,
                          "updated-at": "2022-11-12T08:18:47.372Z"
                      },
                      "relationships": {
                          "field": {
                              "data": {
                                  "id": "1789",
                                  "type": "fields"
                              }
                          }
                      }
                  },
                  {
                      "id": "8681",
                      "type": "exclusion-rules",
                      "attributes": {
                          "operator": "in",
                          "value": [
                              "Non High School"
                          ],
                          "position": 6,
                          "updated-at": "2022-11-12T08:18:47.372Z"
                      },
                      "relationships": {
                          "field": {
                              "data": {
                                  "id": "1790",
                                  "type": "fields"
                              }
                          }
                      }
                  }
              ]
          });
        }),
      ],
    },
  }
}

export const FPIDialog = {
  ...Default,
  play: async ({canvasElement}) => {
    const canvas = within(canvasElement);
    await canvas.findByText("Form Posting Setup")
    const button = await canvas.findByLabelText('Send Form Posting Instructions');
    await userEvent.click(button);
    await screen.findByText("Send Form Posting Instructions");
  },
}
